/**
 * Helper partagé pour la redirection post-authentification ou post-choix de
 * domaine via le paramètre `?next=...`.
 *
 * Pourquoi un module dédié : la logique d'extraction et de validation est
 * utilisée à 3 endroits (`Login.tsx`, `ChoisirDomaine.tsx`, et la garde
 * `RequireAuth` qui le génère). Sans factorisation, le risque est qu'une
 * faille open-redirect soit corrigée à un seul endroit.
 *
 * Règle anti open-redirect : on n'accepte que les chemins relatifs internes
 * (commencent par `/` mais pas `//`). Tout le reste est ignoré et on tombe
 * sur le fallback (typiquement `/dashboard`). Cf OWASP Open Redirect.
 *
 *   ?next=/parametres/membres           → /parametres/membres        ✓
 *   ?next=/parametres?onglet=roles      → /parametres?onglet=roles   ✓
 *   ?next=//evil.com                    → /dashboard (fallback)      ✗
 *   ?next=/\evil.com                    → /dashboard (cf rejet \)    ✗
 *   ?next=https://evil.com              → /dashboard                 ✗
 *   ?next=javascript:alert(1)           → /dashboard                 ✗
 */

const DEFAULT_FALLBACK = "/dashboard";

/**
 * Extrait `?next=` d'une URL (ou string) et retourne soit la cible relative
 * sûre, soit `fallback` (`/dashboard` par défaut).
 */
export function safeNextFromSearch(
  search: string | null | undefined,
  fallback: string = DEFAULT_FALLBACK,
): string {
  if (!search) return fallback;
  const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("next");
  return validateNextTarget(raw, fallback);
}

/** Variante qui prend directement la valeur brute du paramètre `next`. */
export function validateNextTarget(
  rawNext: string | null | undefined,
  fallback: string = DEFAULT_FALLBACK,
): string {
  if (!rawNext) return fallback;
  // Doit être un chemin relatif interne. Refuse `//evil.com`, `https://...`,
  // `javascript:...`, etc. (le seul chemin qui commence par `/` sans `//` est
  // un chemin interne).
  if (!rawNext.startsWith("/") || rawNext.startsWith("//")) return fallback;
  // Le `:` est interdit UNIQUEMENT dans la partie chemin (avant `?`). Dans la
  // query string, il est légitime (timestamps ISO 8601 `?date=2026-05-10T12:00`,
  // identifiants `urn:...`, etc.) et un rejet global produirait des
  // redirections silencieuses vers /dashboard sur des liens valides. Le `\`
  // est interdit partout (Chrome historiquement normalisait `/\evil.com` en
  // `//evil.com` côté URL bar).
  const queryStart = rawNext.indexOf("?");
  const pathPart = queryStart === -1 ? rawNext : rawNext.slice(0, queryStart);
  if (rawNext.includes("\\") || pathPart.includes(":")) return fallback;
  return rawNext;
}

/**
 * Construit la valeur à passer en `?next=` quand on redirige depuis une page
 * protégée. On capture le pathname + search, jamais le hash (qui n'est pas
 * envoyé au serveur de toute façon).
 *
 * À utiliser dans `RequireAuth` ou `AppShellLayout` quand on doit envoyer
 * l'utilisateur vers /login ou /choisir-domaine en mémorisant où il voulait
 * aller.
 */
export function buildNextParam(location: { pathname: string; search?: string }): string {
  const search = location.search ?? "";
  return `${location.pathname}${search}`;
}

/**
 * Construit l'URL de redirection vers une route sas (`/login`, `/choisir-domaine`,
 * etc.) en ajoutant `?next=...` UNIQUEMENT si la cible diffère du fallback
 * `/dashboard`. Sinon on retourne juste la base — l'URL reste lisible.
 *
 *   appendNextIfMeaningful("/choisir-domaine", "/dashboard")
 *   → "/choisir-domaine"
 *
 *   appendNextIfMeaningful("/choisir-domaine", "/parametres/membres")
 *   → "/choisir-domaine?next=%2Fparametres%2Fmembres"
 */
export function appendNextIfMeaningful(baseUrl: string, nextTarget: string): string {
  if (!nextTarget || nextTarget === DEFAULT_FALLBACK) return baseUrl;
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}next=${encodeURIComponent(nextTarget)}`;
}
